import { Component } from '@angular/core';
import {
  Answer,
  AssetType,
  Exercise,
  NxTestSessionDetailedResultV2Model,
  StudentResultsClient,
  ExamReportsClient,
  UserAnswer,
  AnswerOption,
  Question,
} from '../../../generated-client/generated-api-client';
import { SimpleModalComponent } from 'ngx-simple-modal';
import { ToastrService } from 'ngx-toastr';
import { ErrorProcessorService } from '../../../services/error-processor.service';
import { AnswerColor } from '../../../enums/AnswerColor';
import { StudentExamDataService } from '../../../services/data-services/student-exam-data.service';
import { jsPDF } from 'jspdf';
import '../../../../assets/fonts/Roboto-Regular-normal.js';

export interface DetailedResultV2PreviewModal {
  resultId: string;
  courseTitle: string;
  studentName: string;
}

@Component({
  selector: 'app-detailed-resultV2-preview-modal',
  templateUrl: './detailed-resultV2-preview-modal.component.html',
  styleUrls: ['./detailed-resultV2-preview-modal.component.sass'],
})
export class DetailedResultV2PreviewModalComponent
  extends SimpleModalComponent<DetailedResultV2PreviewModal, boolean>
  implements DetailedResultV2PreviewModal
{
  AnswerColor = AnswerColor;
  model?: NxTestSessionDetailedResultV2Model;
  resultId!: string;
  isLoading: boolean = true;
  precision: number = 0;
  courseTitle!: string;
  isPdfLoading = false;
  studentName!: string;

  constructor(
    private _toastr: ToastrService,
    private _studentResultsClient: StudentResultsClient,
    private _examReportsClient: ExamReportsClient,
    private _studentExamDataService: StudentExamDataService,
    private _errorProcessor: ErrorProcessorService
  ) {
    super();
  }

  ngOnInit(): void {
    this._examReportsClient.get(this.examReportId).subscribe(next => {
      this.precision = next.percentagePrecision || 0;
    });
    this._studentResultsClient.detailedResultV2(this.resultId).subscribe(
      model => {
        this.model = model;
        this.isLoading = false;
      },
      error => {
        this._toastr.error('Váratlan hiba történt');
        this.close();
      }
    );
  }

  get AssetType() {
    return AssetType;
  }

  get examReportId() {
    return this._studentExamDataService.examReportId.value;
  }

  save() {
    this._studentResultsClient.detailedResultV2(this.resultId).subscribe(
      next => {
        const dataStr = 'data:text/json;charset=utf-8,' + encodeURIComponent(JSON.stringify(next));
        const dlAnchorElem = document.getElementById('downloadAnchorElem') as HTMLAnchorElement;
        dlAnchorElem.setAttribute('href', dataStr);
        dlAnchorElem.setAttribute('download', `eredmény-${this.resultId}.json`);
        dlAnchorElem.click();
      },
      error => {
        const statusMessages = {
          404: 'Nem található részletes eredmény fájl.',
        };
        return this._errorProcessor.processError(error, true, false, statusMessages);
      }
    );
  }

  formatResult(data: number) {
    return (Math.trunc(data * Math.pow(10, this.precision)) / Math.pow(10, this.precision)).toFixed(this.precision);
  }

  getQuestionText(options: AnswerOption[] | undefined, id: number | undefined) {
    return options?.filter(option => option.id == id)[0].description;
  }

  getMultiChoiceAnswerOptionColor(questions: Question[] | undefined, id: number | undefined) {
    var color;
    if (questions == undefined || id == undefined) return 'red';
    questions[0]?.correctAnswers?.indexOf(id) != undefined && questions[0]?.correctAnswers?.indexOf(id) > -1
      ? (color = 'green')
      : (color = 'red');
    return color;
  }

  geFillInSelectAnswerOptionColor(questions: Question[] | undefined, id: number | undefined) {
    var color;
    if (questions == undefined || id == undefined) return 'red';
    questions[0]?.correctAnswers?.indexOf(id) != undefined && questions[0]?.correctAnswers?.indexOf(id) > -1
      ? (color = 'green')
      : (color = 'red');
    return color;
  }

  answerColor(exercise: Exercise, answer: Answer, userAnswers: UserAnswer[] | undefined): AnswerColor {
    let result = AnswerColor.Default;
    let userAnswerIds: number[] = [];
    const userAnswer = userAnswers?.find(x => x.exerciseId == exercise.id);

    if (answer.id === undefined) return result;

    // helyes válaszok zöldre színezése
    if (exercise.correctAnswerId != undefined && exercise.correctAnswerId >= 0) {
      if (answer.id == exercise.correctAnswerId) {
        result = AnswerColor.Green;
      }
    }

    if (exercise.correctAnswerIds) {
      if (exercise.correctAnswerIds.includes(answer.id!)) {
        result = AnswerColor.Green;
      }
    }

    // vizsgázó válaszait érintő színezések
    if (userAnswer) {
      if (userAnswer.answerId !== undefined && userAnswer.answerId !== null && userAnswer.answerId >= 0) {
        userAnswerIds = [userAnswer.answerId];
      }
      if (userAnswer.answerIds) {
        userAnswerIds = userAnswer.answerIds;
      }
      if (userAnswer.fillInAnswers && userAnswer.fillInAnswers.length > 0) {
        const correctFillInAnswerIds = userAnswer.fillInAnswers
          .filter(x => x.isCorrect && x.answerId != undefined)
          .map(x => x.answerId!);
        if (correctFillInAnswerIds && correctFillInAnswerIds.length > 0) {
          userAnswerIds = correctFillInAnswerIds;
        }

        if (userAnswerIds.includes(answer.id)) {
          result = AnswerColor.GreenWithTick;
        } else {
          result = AnswerColor.Red;
        }
      }

      if (result === AnswerColor.Green && userAnswerIds.includes(answer.id)) {
        result = AnswerColor.GreenWithTick;
      } else if (
        result !== AnswerColor.Green &&
        result !== AnswerColor.GreenWithTick &&
        userAnswerIds.includes(answer.id)
      ) {
        result = AnswerColor.Red;
      }
    }
    return result;
  }

  exportPdf() {
    const infoToast = this._toastr.info('PDF generálás folyamatban...', undefined, { timeOut: 0, closeButton: true });
    this.isPdfLoading = true;

    try {
      const pages = document.querySelector('.modal-body') as HTMLElement;
      const rect = pages.getBoundingClientRect();

      const doc = new jsPDF({
        orientation: 'p',
        unit: 'px',
        format: [rect.width, rect.width / 0.7],
        putOnlyUsedFonts: true,
        compress: true,
      });

      doc.setFont('Roboto-Regular');

      doc.html(pages, {
        html2canvas: {
          letterRendering: true,
          removeContainer: true,
          onclone: (clonedDoc: Document) => {
            // Apply styling to the cloned document
            const modalBody = clonedDoc.querySelector('.modal-body');
            if (modalBody) {
              // Add global styles
              const style = clonedDoc.createElement('style');
              style.textContent = `
                * {
                  font-family: 'Roboto-Regular', sans-serif !important;
                  letter-spacing: normal !important;
                  word-spacing: normal !important;
                  white-space: normal !important;
                }
                p, div, span, td, h2 {
                  word-break: break-word !important;
                  overflow-wrap: break-word !important;
                }
              `;
              clonedDoc.head.appendChild(style);

              // Remove material icons from the clone
              const materialIcons = clonedDoc.querySelectorAll('.material-icons, .material-icons-outlined');
              materialIcons.forEach(icon => {
                (icon as HTMLElement).style.display = 'none';
                // Alternative: icon.remove();
              });

              // Remove image containers from the clone
              const imageContainers = clonedDoc.querySelectorAll('.img-container');
              imageContainers.forEach(container => {
                container.remove();
              });

              // Process text nodes to ensure whitespace preservation
              const processTextNodes = (node: Node) => {
                if (node.nodeType === Node.TEXT_NODE && node.textContent) {
                  // Preserve whitespace by adding non-breaking space entity where needed
                  // Replace multiple spaces with non-breaking spaces to preserve them
                  node.textContent = node.textContent
                    .replace(/ {2,}/g, (match: string) => ' ' + '\u00A0'.repeat(match.length - 1))
                    // Add optional break opportunities after specific characters
                    .replace(/([\/\-\.])/g, '$1\u200B');
                } else if (node.hasChildNodes()) {
                  node.childNodes.forEach(processTextNodes);
                }
              };

              // Process all nodes in the modal body
              processTextNodes(modalBody);

              // Set CSS styles on specific elements
              modalBody.querySelectorAll('*').forEach(el => {
                if (el instanceof HTMLElement) {
                  el.style.textRendering = 'optimizeLegibility';
                  el.style.fontVariantLigatures = 'none';
                }
              });
            }
          },
        },
        callback: (doc: jsPDF) => {
          doc.save(`${this.studentName}_${this.courseTitle}.pdf`);
          this.isPdfLoading = false;
          this._toastr.clear(infoToast.toastId);
        },
      });
    } catch (e) {
      this.isPdfLoading = false;
      this._toastr.clear(infoToast.toastId);
      this._toastr.error('A PDF generálása nem sikerült. Kérjük, próbálja újra.');
    }
  }
}
